import React, { useEffect, useState } from "react";
import styles from './Reports.module.scss';
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import PageTitle from "../../../components/pageTitle/PageTitle";
import Dropdown from "../../../components/dropdown/Dropdown";
import axiosClient from "../../../api/axiosClient";
import Button from "../../../components/button/Button";
import { useToasts } from "../../../contexts/ToastContext";
import { useProgram } from "../../../contexts/ProgramContext";
import MultiSelectDropdown from "../../../components/multiSelectDropdown/MultiSelectDropdown";
import DropDownButton from "../../../components/dropDownButton/DropDownButton";

const Reports = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [programYearList, setProgramYearList] = useState([]);
  const [programYearId, setProgramYearId] = useState(null);
  const [semesterId, setSemesterId] = useState(null);
  const [specialitiesList, setSpecialitiesList] = useState([]);
  const [specialityIds, setSpecialityIds] = useState([]);
  const [clinicalSiteList, setClinicalSiteList] = useState([]);
  const [clinicalSiteIds, setClinicalSiteIds] = useState([]);

  const { programs: programList, selectedProgramId, setSelectedProgramId } = useProgram();

  const { showToast } = useToasts();

  useEffect(() => {
    if (!selectedProgramId) return;
    axiosClient.get('years/details', { params: { programId: selectedProgramId } }).then((response) => {
      setProgramYearList(response.data.map((item) => ({
        value: item.year.id,
        label: item.year.year,
        semesters: item.semesters
      })));
    }).catch((error) => { showToast(error.message, 'error'); });
  }, [selectedProgramId, showToast]);

  useEffect(() => {
    axiosClient.get('specialities').then((response) => {
      if (response.data) {
        setSpecialitiesList(response.data.map((item) => ({
          value: item.id,
          label: item.name
        })));
      }
    });
  }, []);

  useEffect(() => {
    axiosClient.get('clinical-sites/findAll').then((response) => {
      setClinicalSiteList(response.data.map((item) => ({
        value: item.id,
        label: item.name
      })));
    });
  }, []);

  const getSemesters = () => {
    if (programYearId) {
      const selectedYearData = programYearList.find(item => item.value === parseInt(programYearId));
      if (selectedYearData?.semesters) {
        return selectedYearData.semesters.map((item) => ({
          value: item.id,
          label: item.semester
        }));
      }
    }
    return [];
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    const params = { reportName: 'COHORT_EVALUATION_SUMMARY' };
    if (selectedProgramId) params.programId = selectedProgramId;
    if (programYearId) params.year = programYearId;
    if (semesterId) params.semester = semesterId;
    if (specialityIds.length > 0) params.speciality = specialityIds;
    if (clinicalSiteIds.length > 0) params.clinicalSite = clinicalSiteIds;
    const link = document.createElement('a');
    link.download = 'cohort_evaluation_summary.xlsx';

    await axiosClient.post('reports/evaluation-reports', params, {
      responseType: 'blob',
      transformResponse: [(data) => data],
    })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response]));
        link.href = url;
        link.click();
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const isValid = programYearId || semesterId || specialityIds.length > 0 || clinicalSiteIds.length > 0;

  const selectedProgramTitle = programList.find(p => p.id === selectedProgramId)?.title || 'Select Program';
  const programOptions = programList.map(p => ({ label: p.title, onClick: () => setSelectedProgramId(p.id) }));

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <PageTitle label="Reports" />
          <DropDownButton
            label={selectedProgramTitle}
            items={programOptions}
            type="button"
            size="small"
            variant="primary-light"
            position="bottom-left"
          />
        </div>
      </div>
      <div className={styles.container}>
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className={styles.row} style={{ alignItems: 'center' }}>
            <div className={styles.column20}>
              <h4 className={styles.type}>Select Report Type</h4>
            </div>
            <div className={styles.column50}>
              <Dropdown
                options={[{ value: 'CES', label: 'Cohort Evaluation Summary Report' }]}
                value="CES"
                onChange={() => {}}
              />
            </div>
          </div>
          <hr />
          <div className={styles.row}>
            <div className={styles.column50}>
              <Dropdown
                label="Program Year"
                options={programYearList}
                value={programYearId}
                onChange={(selected) => {
                  setProgramYearId(prevValue => {
                    if (prevValue !== selected) setSemesterId(null);
                    return selected;
                  });
                }}
              />
            </div>
            <div className={styles.column50}>
              <Dropdown
                label="Semester"
                options={getSemesters()}
                value={semesterId}
                onChange={(selected) => setSemesterId(selected)}
              />
            </div>
            <div className={styles.column50}>
              <MultiSelectDropdown
                label="Specialty"
                options={specialitiesList}
                selected={specialityIds}
                onChange={(selected) => setSpecialityIds(selected)}
              />
            </div>
            <div className={styles.column50}>
              <MultiSelectDropdown
                label="Clinical Site"
                options={clinicalSiteList}
                selected={clinicalSiteIds}
                onChange={(selected) => setClinicalSiteIds(selected)}
              />
            </div>
          </div>
          <div className={styles.actions}>
            <Button
              label="Export Report"
              variant="primary"
              size="medium"
              type="submit"
              disabled={!isValid}
            />
          </div>
        </form>
      </div>
    </>
  );
};

export default Reports;
